import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Flame, ChevronRight } from 'lucide-react';
import { trendingApi } from '../../lib/api';
import FavoriteButton from '../favorites/FavoriteButton';
import { useFavoritedIds } from '../../hooks/useFavoritedIds';

const TrendingCard = ({ item, rank, showRankBadge, favorited }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      data-testid={`trending-card-${item.id.replace(/\W+/g, '-')}`}
    >
      <Card className="technical-card h-full border-autoxium-blue/30 overflow-hidden relative">
        {item.contentType && (
          <FavoriteButton
            contentType={item.contentType}
            contentId={item.contentId}
            initialFavorited={favorited}
            title={item.title}
            className="absolute top-3 right-3 z-10 bg-white/80 hover:bg-white"
          />
        )}
        {item.image && (
          <img src={item.image} alt="" className="w-full h-40 object-cover" />
        )}
        <CardContent className="p-6 flex flex-col h-full">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {showRankBadge && (
              <Badge
                variant="outline"
                className="w-fit font-mono text-xs uppercase tracking-wider border-autoxium-blue/50 text-autoxium-blue"
              >
                <Flame className="h-3 w-3 mr-1" />
                #{rank} Trending
              </Badge>
            )}
            {item.badge && (
              <Badge variant="outline" className="w-fit font-mono text-xs uppercase tracking-wider">
                {item.badge}
              </Badge>
            )}
          </div>
          {Icon && !item.image && (
            <div className="h-10 w-10 rounded-md bg-chassis flex items-center justify-center mb-4">
              <Icon className="h-5 w-5 text-autoxium-blue" />
            </div>
          )}
          <h3 className="font-chivo text-lg font-bold mb-2 line-clamp-2">{item.title}</h3>
          <p className="text-muted-foreground text-sm mb-4 line-clamp-3 flex-1">{item.description}</p>
          <div className="flex items-center justify-between pt-4 border-t border-structure">
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              {item.meta && <span>{item.meta}</span>}
            </div>
            <ChevronRight className="h-4 w-4 text-autoxium-blue" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

/**
 * Top-N-most-visited-in-30-days section, reused across Articles/How Does It Work?/
 * Simulation/Tools, and (with a smaller `topN`) the How Does It Work? section/
 * subsection pages. `items` must already be in the shared shape below — each page
 * maps its own data into it before rendering this component:
 *   { id, to, title, description, badge?, meta?, image?, icon?, updatedAt? }
 * `id` doubles as the visit-tracking item_id (see PageVisitTracker) and must match the
 * item's actual detail-page pathname. `updatedAt` (ISO date/datetime) is only used as a
 * tie-breaker; omit it if the item has no natural date.
 *
 * Renders nothing while `items` is empty. If the backend reports the section/scope has
 * never recorded a single visit (see /api/trending/rank), the default behavior is to
 * render nothing (there's nothing meaningful to rank) — pass `fallbackWhenEmpty` to
 * instead show the `topN` most-recently-updated items with no trending badge, so a
 * caller that needs a guaranteed non-empty row (e.g. "top topics" above a "More Topics"
 * link) doesn't end up with a blank gap for a brand-new, not-yet-visited scope.
 */
const TrendingSection = ({
  sectionType,
  items,
  topN = 9,
  fallbackWhenEmpty = false,
  title = 'Trending',
  fallbackTitle = 'Recently Added',
  testId = sectionType,
  // For nesting under a heading a caller already renders (How Does It Work? category/
  // subcategory blocks): drops this component's own page-level top padding and
  // re-nested max-width container (the caller already provides both), and always
  // labels the heading "Top Topics" regardless of ranked-vs-fallback state — ignoring
  // `title`/`fallbackTitle` entirely, so a caller can't accidentally swap them the way
  // the two "How Does It Work?" call sites originally did.
  inline = false,
  // The fixed heading used in `inline` mode ("Top Articles" on the Articles sections).
  inlineTitle = 'Top Topics',
}) => {
  const [rankedIds, setRankedIds] = useState(null);

  // Every item in a given TrendingSection instance is the same favoritable content
  // type in practice (Articles' Featured cards opt out by omitting contentType, and
  // Simulation/Tools items never carry one) -- one batch check covers the row.
  const favoriteContentType = (items || []).find((i) => i.contentType)?.contentType;
  const favoriteContentIds = favoriteContentType
    ? (items || []).filter((i) => i.contentType === favoriteContentType).map((i) => i.contentId)
    : [];
  const favoritedIds = useFavoritedIds(favoriteContentType, favoriteContentIds);

  useEffect(() => {
    if (!items || items.length === 0) {
      setRankedIds([]);
      return;
    }
    let cancelled = false;
    setRankedIds(null);
    trendingApi
      .rank(sectionType, items.map((i) => ({ item_id: i.id, updated_at: i.updatedAt || null })))
      .then((ids) => {
        if (!cancelled) setRankedIds(ids);
      })
      .catch(() => {
        if (!cancelled) setRankedIds([]);
      });
    return () => {
      cancelled = true;
    };
  }, [sectionType, items]);

  const isEmptyResult = rankedIds !== null && rankedIds.length === 0;
  const useFallback = isEmptyResult && fallbackWhenEmpty && items && items.length > 0;

  if (isEmptyResult && !useFallback) return null;

  const byId = new Map(items.map((i) => [i.id, i]));
  let trendingItems = null;
  let showRankBadge = true;
  if (useFallback) {
    trendingItems = [...items]
      .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
      .slice(0, topN);
    showRankBadge = false;
  } else if (rankedIds !== null) {
    trendingItems = rankedIds.slice(0, topN).map((id) => byId.get(id)).filter(Boolean);
  }

  if (trendingItems !== null && trendingItems.length === 0) return null;

  const heading = inline ? inlineTitle : useFallback ? fallbackTitle : title;

  const content = (
    <>
      <div className={`flex items-center gap-2 ${inline ? 'mb-6' : 'mb-8'}`}>
        <Flame className="h-5 w-5 text-autoxium-blue" />
        <h2 className="font-chivo text-2xl font-bold uppercase tracking-tight">{heading}</h2>
      </div>
      {trendingItems === null ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="technical-card">
              <CardContent className="p-6">
                <Skeleton className="h-4 w-24 mb-4" />
                <Skeleton className="h-6 w-full mb-2" />
                <Skeleton className="h-4 w-full mb-4" />
                <Skeleton className="h-4 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {trendingItems.map((item, index) => (
            <TrendingCard
              key={item.id}
              item={item}
              rank={index + 1}
              showRankBadge={showRankBadge}
              favorited={favoritedIds.has(item.contentId)}
            />
          ))}
        </div>
      )}
    </>
  );

  if (inline) {
    return <div data-testid={`trending-section-${testId}`}>{content}</div>;
  }

  return (
    <section className="py-12 md:py-16 pb-0" data-testid={`trending-section-${testId}`}>
      <div className="max-w-screen-2xl mx-auto px-4 md:px-8">{content}</div>
    </section>
  );
};

export default TrendingSection;
